import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Scale, User, ChevronDown, CheckCircle, Shield, Sparkles, Mail
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [resetLink, setResetLink] = useState('');
  const [focusedField, setFocusedField] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { username, password });
      login(res.data);
      toast.success('Accesso effettuato!');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Credenziali non valide');
    } finally {
      setLoading(false);
    }
  };

  const seedData = async () => {
    try {
      const res = await axios.post(`${API}/seed`);
      toast.success('Utenti di test creati! Usa notaio/notaio123 o impiegato/impiegato123');
    } catch (err) {
      toast.info('Utenti già presenti');
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) {
      toast.error('Inserisci la tua email');
      return;
    }
    setForgotLoading(true);
    try {
      const res = await axios.post(`${API}/auth/request-reset`, { email: forgotEmail });
      setForgotSuccess(true);
      if (res.data.demo_info?.reset_link) {
        setResetLink(res.data.demo_info.reset_link);
      }
      toast.success('Richiesta inviata!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore durante la richiesta');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1675117570059-a20de3a15272?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwzfHxpdGFsaWFuJTIwYXJjaGl0ZWN0dXJlJTIwbWFyYmxlJTIwY29sdW1ucyUyMGVsZWdhbnQlMjBpbnRlcmlvciUyMHBhbGF6em98ZW58MHx8fHwxNzY4NTUwNzI1fDA&ixlib=rb-4.1.0&q=85')`
          }}
        />
        {/* Overlay Gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/80 via-slate-800/70 to-amber-900/60" />
        
        {/* Content on Image */}
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          {/* Logo Header - Su una riga */}
          <div>
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
                <Scale className="w-8 h-8 text-amber-400" />
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-light tracking-wide">Studio Notarile</span>
                <span className="text-3xl font-bold bg-gradient-to-r from-amber-400 to-amber-200 bg-clip-text text-transparent">AI</span>
              </div>
            </div>
            <div className="mt-3 ml-[72px] w-32 h-0.5 bg-gradient-to-r from-amber-400/80 to-transparent rounded-full" />
          </div>
          
          <div className="space-y-6">
            <blockquote className="text-4xl font-light leading-relaxed text-white/95">
              "Il futuro del notariato<br/>è qui"
            </blockquote>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-400 to-amber-600 rounded-full" />
            <p className="text-white/70 text-lg max-w-md leading-relaxed">
              Gestione completa dello studio notarile con intelligenza artificiale integrata
            </p>
          </div>
          
          <div className="flex items-center gap-8 text-white/60 text-sm">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              <span>Sicurezza Garantita</span>
            </div>
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              <span>AI Integrata</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-gradient-to-br from-slate-50 via-white to-slate-100">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="p-2 bg-slate-100 rounded-lg">
                <Scale className="w-6 h-6 text-slate-700" />
              </div>
              <span className="text-2xl font-light text-slate-800">Studio Notarile</span>
              <span className="text-2xl font-bold bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">AI</span>
            </div>
            <p className="text-slate-500 text-sm">"Il futuro del notariato è qui"</p>
          </div>

          {/* Glass Card */}
          <div className="relative">
            {/* Decorative elements */}
            <div className="absolute -top-4 -left-4 w-24 h-24 bg-amber-400/20 rounded-full blur-2xl" />
            <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-slate-400/20 rounded-full blur-2xl" />
            
            <div className="relative backdrop-blur-sm bg-white/80 rounded-2xl shadow-2xl shadow-slate-200/50 border border-white/50 p-8">
              {/* Logo */}
              <div className="text-center mb-8">
                <div className="relative inline-block">
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full blur-lg opacity-30 animate-pulse" />
                  <img 
                    src="/logo-notaio.jpg" 
                    alt="Logo Studio Notarile" 
                    className="relative w-20 h-20 object-contain rounded-full shadow-lg border-2 border-white"
                  />
                </div>
                <h1 className="mt-4 text-2xl font-semibold text-slate-800">Benvenuto</h1>
                <p className="text-slate-500 text-sm mt-1">Accedi al tuo account</p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="username" className="text-slate-700 font-medium">
                    Username
                  </Label>
                  <div className={`relative transition-all duration-300 ${focusedField === 'username' ? 'transform scale-[1.02]' : ''}`}>
                    <User className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 transition-colors duration-300 ${focusedField === 'username' ? 'text-amber-500' : 'text-slate-400'}`} />
                    <Input
                      id="username"
                      data-testid="username-input"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      onFocus={() => setFocusedField('username')}
                      onBlur={() => setFocusedField(null)}
                      placeholder="Inserisci username"
                      className="pl-11 h-12 bg-slate-50/50 border-slate-200 focus:border-amber-400 focus:ring-amber-400/20 transition-all duration-300"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-700 font-medium">
                    Password
                  </Label>
                  <div className={`relative transition-all duration-300 ${focusedField === 'password' ? 'transform scale-[1.02]' : ''}`}>
                    <Shield className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 transition-colors duration-300 ${focusedField === 'password' ? 'text-amber-500' : 'text-slate-400'}`} />
                    <Input
                      id="password"
                      type="password"
                      data-testid="password-input"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onFocus={() => setFocusedField('password')}
                      onBlur={() => setFocusedField(null)}
                      placeholder="Inserisci password"
                      className="pl-11 h-12 bg-slate-50/50 border-slate-200 focus:border-amber-400 focus:ring-amber-400/20 transition-all duration-300"
                      required
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-12 bg-gradient-to-r from-slate-800 to-slate-900 hover:from-slate-700 hover:to-slate-800 text-white font-medium shadow-lg shadow-slate-300/30 transition-all duration-300 hover:shadow-xl hover:shadow-slate-300/40 hover:-translate-y-0.5" 
                  disabled={loading} 
                  data-testid="login-button"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Accesso in corso...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      Accedi
                      <ChevronDown className="w-4 h-4 rotate-[-90deg]" />
                    </span>
                  )}
                </Button>

                <div className="text-center">
                  <Button 
                    type="button" 
                    variant="link" 
                    onClick={() => setShowForgotPassword(true)} 
                    className="text-sm text-amber-600 hover:text-amber-700 font-medium"
                  >
                    Password dimenticata?
                  </Button>
                </div>
              </form>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200"></div>
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-3 text-slate-400">oppure</span>
                </div>
              </div>

              {/* Demo Button */}
              <Button 
                variant="outline" 
                onClick={seedData} 
                className="w-full h-11 border-dashed border-slate-300 text-slate-600 hover:bg-slate-50 hover:border-slate-400"
              >
                <Sparkles className="w-4 h-4 mr-2 text-amber-500" />
                Crea utenti di test
              </Button>

              {/* Footer */}
              <div className="mt-8 pt-6 border-t border-slate-100">
                <a 
                  href="https://telcavoip.ai" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 text-sm text-slate-500 hover:text-teal-600 transition-colors group"
                >
                  <span className="group-hover:animate-pulse">❤️</span>
                  <span>Powered With Love By <strong className="text-teal-500 group-hover:text-teal-600">TelcaVoIP AI</strong></span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Dialog Password Dimenticata */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-amber-500" />
              Password Dimenticata
            </DialogTitle>
          </DialogHeader>
          
          {forgotSuccess ? (
            <div className="space-y-4">
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
                <p className="text-green-700 font-medium text-lg">Richiesta inviata!</p>
                <p className="text-sm text-slate-500 mt-2">
                  Se l'email è registrata, riceverai le istruzioni per il reset.
                </p>
              </div>
              
              {resetLink && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Mail className="w-4 h-4 text-amber-600" />
                    <p className="text-sm font-medium text-amber-800">DEMO MODE</p>
                  </div>
                  <p className="text-xs text-amber-700 mb-3">
                    L'email non viene inviata realmente. Usa questo link per resettare:
                  </p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full border-amber-300 text-amber-700 hover:bg-amber-100"
                    onClick={() => {
                      navigate(resetLink);
                      setShowForgotPassword(false);
                    }}
                  >
                    Vai al Reset Password
                  </Button>
                </div>
              )}
              
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => {
                  setShowForgotPassword(false);
                  setForgotSuccess(false);
                  setForgotEmail('');
                  setResetLink('');
                }}
              >
                Chiudi
              </Button>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <p className="text-sm text-slate-500">
                Inserisci la tua email e riceverai le istruzioni per reimpostare la password.
              </p>
              <div className="space-y-2">
                <Label htmlFor="forgot-email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="forgot-email"
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="La tua email"
                    className="pl-11"
                    required
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setShowForgotPassword(false)}
                >
                  Annulla
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700" 
                  disabled={forgotLoading}
                >
                  {forgotLoading ? 'Invio...' : 'Invia Richiesta'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoginPage;
